import { useState } from 'react'
import type { FC } from 'react'

import { F0Box, F0Card, F0Text } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import {
  PerformancePastReviewsTabDocument,
  ReviewAnswerEvaluationFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { BoxedCallout } from 'modules/performance_process/components/common/BoxedCallout'
import { mapQuestionnaireResponse } from 'modules/performance_process/lib/questionnaireForStrategies'
import { QuestionnaireResponse } from 'modules/questionnaires/components/QuestionnaireResponse'

import { EvaluationCard } from './EvaluationCard'

const EmptyState = () => (
  <F0Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="sm"
    padding="xl"
  >
    <F0Text
      variant="body"
      content={i18n.t(
        'performance_process.reviews.answer_evaluation.past_reviews_section.empty_state.title'
      )}
    />
    <F0Text
      variant="description"
      content={i18n.t(
        'performance_process.reviews.answer_evaluation.past_reviews_section.empty_state.description'
      )}
    />
  </F0Box>
)

const LoadingState = () => (
  <F0Box display="flex" flexDirection="column" gap="md">
    <F0Card.Skeleton key={`goals-skeleton-1`} compact />
    <F0Card.Skeleton key={`goals-skeleton-2`} compact />
  </F0Box>
)

export const PastReviewsTab: FC<{
  targetAccessId: string
  reviewProcessStartedAt: string
}> = ({ targetAccessId, reviewProcessStartedAt }) => {
  const [selectedEvaluation, setSelectedEvaluation] = useState<
    ReviewAnswerEvaluationFragment | undefined
  >(undefined)

  const { data, loading } = useQuery(PerformancePastReviewsTabDocument, {
    variables: {
      targetAccessId,
      reviewProcessStartedAt,
    },
  })

  const selectEvaluation = (evaluation: ReviewAnswerEvaluationFragment) => {
    setSelectedEvaluation(evaluation)
  }

  const publishedEvaluations = data?.performance.reviewEvaluationsConnection.nodes ?? []
  const activeEvaluation = selectedEvaluation ?? publishedEvaluations[0]
  const selectedEvaluationAnswers = activeEvaluation?.answeredQuestionnaires

  if (loading) {
    return <LoadingState />
  }

  if (!publishedEvaluations.length) {
    return <EmptyState />
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="xl">
      <F0Box display="flex" flexDirection="row" gap="md">
        {publishedEvaluations.map((evaluation) => (
          <EvaluationCard
            key={evaluation.id}
            evaluation={evaluation}
            isSelected={activeEvaluation?.id === evaluation.id}
            onSelect={() => selectEvaluation(evaluation)}
          />
        ))}
      </F0Box>

      <F0Box borderTop="default" marginY="md" />

      {activeEvaluation ? (
        <>
          <QuestionnaireResponse
            key={`${activeEvaluation.id}-answered-questionnaire`}
            readonly
            content={mapQuestionnaireResponse(
              selectedEvaluationAnswers?.answeredQuestionnaireWithSections
            )}
            onAnswer={() => null}
            emptyState={<></>}
          />

          <QuestionnaireResponse
            key={`${activeEvaluation.id}-answered-competencies-questionnaire`}
            readonly
            content={mapQuestionnaireResponse(
              selectedEvaluationAnswers?.answeredCompetenciesQuestionnaire
            )}
            onAnswer={() => null}
            emptyState={<></>}
          />

          <QuestionnaireResponse
            key={`${activeEvaluation.id}-answered-employee-score-questionnaire`}
            readonly
            content={mapQuestionnaireResponse(
              selectedEvaluationAnswers?.answeredEmployeeScoreQuestionnaire
            )}
            onAnswer={() => null}
            emptyState={<></>}
          />

          <QuestionnaireResponse
            key={`${activeEvaluation.id}-answered-employee-potential-score-questionnaire`}
            readonly
            content={mapQuestionnaireResponse(
              selectedEvaluationAnswers?.answeredEmployeePotentialScoreQuestionnaire
            )}
            onAnswer={() => null}
            emptyState={<></>}
            renderSectionFooter={() => (
              <BoxedCallout
                description={i18n.t(
                  'performance_process.employee_potential_score.questionnaire.callout'
                )}
              />
            )}
          />
        </>
      ) : (
        <F0Box display="flex" justifyContent="center" paddingY="xl">
          <Spinner />
        </F0Box>
      )}
    </F0Box>
  )
}
